import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Plus, X, User, Phone, Mail, Baby, Camera, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { compressImage } from '../../lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditarPerfilFamilia = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [responsaveis, setResponsaveis] = useState([]);
  const [filhos, setFilhos] = useState([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChild, setNewChild] = useState({ nome: '', data_nascimento: '', sexo: 'M' });
  const fileInputRefs = useRef({});

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('parent_token')}` });

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (!token) { navigate('/app-pais/login'); return; }

    axios.get(`${API}/parent/me`, { headers: getHeaders() })
      .then(res => {
        const data = res.data;
        const resps = data.responsaveis || [{ nome: data.nome || '', celular: data.celular || '', email: data.email || '', parentesco: data.parentesco || '' }];
        setResponsaveis(resps);
        setFilhos(data.filhos || []);
      })
      .catch(() => { toast.error('Erro ao carregar perfil'); navigate('/app-pais/dashboard'); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const updateResp = (idx, field, value) => {
    setResponsaveis(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addResp = () => {
    if (responsaveis.length >= 3) return;
    setResponsaveis(prev => [...prev, { nome: '', celular: '', email: '', parentesco: '' }]);
  };

  const removeResp = (idx) => {
    if (responsaveis.length <= 1) return;
    setResponsaveis(prev => prev.filter((_, i) => i !== idx));
  };

  const updateFilho = (idx, field, value) => {
    setFilhos(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const handleSave = async () => {
    if (!responsaveis[0]?.nome?.trim()) { toast.error('Nome do responsavel principal e obrigatorio'); return; }
    if (!responsaveis[0]?.celular?.trim()) { toast.error('Celular do responsavel principal e obrigatorio'); return; }

    setSaving(true);
    try {
      await axios.put(`${API}/parent/profile`, { responsaveis, filhos }, { headers: getHeaders() });
      toast.success('Perfil atualizado com sucesso!');
      const stored = JSON.parse(localStorage.getItem('parent_data') || '{}');
      stored.parent_name = responsaveis[0].nome;
      localStorage.setItem('parent_data', JSON.stringify(stored));
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const [uploadingPhoto, setUploadingPhoto] = useState({}); // {childId: bool}

  const handleUploadPhoto = async (childId, file) => {
    if (!file) return;
    // Validate type early
    const validTypes = /^(image\/jpeg|image\/jpg|image\/png|image\/webp|image\/heic|image\/heif)$/i;
    if (file.type && !validTypes.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      toast.error('Formato nao aceito. Use JPG, PNG, WEBP ou HEIC (iPhone).');
      return;
    }
    setUploadingPhoto(prev => ({ ...prev, [childId]: true }));
    try {
      // Compress on client to avoid upload failures with large iPhone photos
      const compressed = await compressImage(file);
      const sizeMB = (compressed.size / (1024 * 1024)).toFixed(1);
      if (compressed.size > 15 * 1024 * 1024) {
        toast.error(`Foto muito grande (${sizeMB}MB). Maximo 15MB. Reduza a resolucao no celular.`);
        return;
      }
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await axios.post(`${API}/parent/upload-photo?child_id=${childId}`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setFilhos(prev => prev.map(f => f.id === childId ? { ...f, foto_url: res.data.foto_url } : f));
      toast.success('Foto atualizada!');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.code === 'ECONNABORTED') {
        toast.error('Tempo esgotado. Verifique sua conexao e tente novamente.');
      } else if (detail) {
        toast.error(detail);
      } else if (e.response?.status === 413) {
        toast.error('Arquivo muito grande para o servidor. Reduza a resolucao.');
      } else {
        toast.error('Erro ao enviar foto. Tente novamente.');
      }
      console.error('Photo upload error:', e);
    } finally {
      setUploadingPhoto(prev => ({ ...prev, [childId]: false }));
    }
  };

  const handleAddChild = async () => {
    if (!newChild.nome.trim()) { toast.error('Nome da crianca e obrigatorio'); return; }
    if (!newChild.data_nascimento) { toast.error('Data de nascimento e obrigatoria'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/parent/add-child`, newChild, { headers: getHeaders() });
      toast.success(`${res.data.nome} adicionado(a) com sucesso!`);
      setShowAddChild(false);
      setNewChild({ nome: '', data_nascimento: '', sexo: 'M' });
      // Reload data
      const meRes = await axios.get(`${API}/parent/me`, { headers: getHeaders() });
      setFilhos(meRes.data.filhos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao adicionar crianca');
    }
    setSaving(false);
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none transition-all";

  if (loading) return <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><p className="text-gray-500">Carregando...</p></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-8">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-4 py-6">
        <div className="max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white mb-3 text-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <h1 className="font-nunito font-bold text-2xl">Editar Perfil da Familia</h1>
          <p className="font-nunito font-bold text-[#F4D35E] text-sm mt-1">Portal da Familia</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 mt-6 space-y-5">
        {/* Responsaveis */}
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <h2 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4 flex items-center gap-2">
            <User className="w-5 h-5" /> Responsaveis
          </h2>
          <div className="space-y-4">
            {responsaveis.map((r, i) => (
              <div key={i} className={`space-y-3 ${i > 0 ? 'pt-4 border-t border-gray-100' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase">{i === 0 ? 'Responsavel Principal' : `Responsavel ${i + 1}`}</span>
                  {i > 0 && (
                    <button onClick={() => removeResp(i)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-resp-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Nome completo</label>
                  <input data-testid={`resp-nome-${i}`} value={r.nome} onChange={e => updateResp(i, 'nome', e.target.value)}
                    placeholder="Nome completo" className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Phone className="w-3 h-3" /> Celular</label>
                    <input data-testid={`resp-celular-${i}`} type="tel" inputMode="numeric" value={r.celular}
                      onChange={e => updateResp(i, 'celular', e.target.value)} placeholder="11999001122" className={inputClass} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Parentesco</label>
                    <select data-testid={`resp-parentesco-${i}`} value={r.parentesco || ''} onChange={e => updateResp(i, 'parentesco', e.target.value)} className={inputClass}>
                      <option value="">Selecione</option>
                      <option value="Pai">Pai</option>
                      <option value="Mãe">Mae</option>
                      <option value="Avô">Avo</option>
                      <option value="Avó">Avo(a)</option>
                      <option value="Tio(a)">Tio(a)</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                  <input data-testid={`resp-email-${i}`} type="email" value={r.email || ''} onChange={e => updateResp(i, 'email', e.target.value)}
                    placeholder="email@exemplo.com" className={inputClass} />
                </div>
              </div>
            ))}
          </div>
          {responsaveis.length < 3 && (
            <button onClick={addResp} className="mt-4 text-[#1A4D2E] text-sm font-semibold flex items-center gap-1 hover:underline" data-testid="add-resp-btn">
              <Plus className="w-4 h-4" /> Adicionar responsavel
            </button>
          )}
        </Card>

        {/* Filhos */}
        <Card className="bg-white rounded-2xl p-5 shadow-sm border border-[#E1ECE5]">
          <h2 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4 flex items-center gap-2">
            <Baby className="w-5 h-5" /> Filhos
          </h2>
          <div className="space-y-5">
            {filhos.map((f, i) => (
              <div key={f.id} className={`space-y-3 ${i > 0 ? 'pt-4 border-t border-gray-100' : ''}`}>
                <span className="text-xs font-bold text-gray-500 uppercase">Filho(a) {i + 1}</span>

                {/* Photo */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {f.foto_url ? (
                      <img src={f.foto_url.startsWith('http') ? f.foto_url : `${process.env.REACT_APP_BACKEND_URL}${f.foto_url}`} alt={f.nome}
                        className="w-20 h-20 rounded-xl object-cover border-2 border-[#E1ECE5]"
                        onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; }} />
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <Camera className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <input type="file" accept="image/*,.heic,.heif" ref={el => fileInputRefs.current[f.id] = el}
                      className="hidden" onChange={e => { if (e.target.files[0]) handleUploadPhoto(f.id, e.target.files[0]); }} />
                    <button onClick={() => fileInputRefs.current[f.id]?.click()}
                      disabled={uploadingPhoto[f.id]}
                      className="absolute -bottom-1 -right-1 bg-[#1A4D2E] text-white rounded-full p-1.5 shadow-md hover:bg-[#2D6A3F] disabled:opacity-60"
                      data-testid={`upload-photo-${i}`}>
                      {uploadingPhoto[f.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    </button>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Nome completo</label>
                    <input data-testid={`filho-nome-${i}`} value={f.nome || ''} onChange={e => updateFilho(i, 'nome', e.target.value)}
                      className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Data de nascimento</label>
                    <input data-testid={`filho-nascimento-${i}`} type="date" value={f.data_nascimento || ''}
                      onChange={e => updateFilho(i, 'data_nascimento', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Sexo</label>
                    <select data-testid={`filho-sexo-${i}`} value={f.sexo || ''} onChange={e => updateFilho(i, 'sexo', e.target.value)} className={inputClass}>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Phone className="w-3 h-3" /> Celular da crianca (opcional)</label>
                  <input data-testid={`filho-celular-${i}`} type="tel" inputMode="numeric"
                    value={f.celular_crianca || ''} onChange={e => updateFilho(i, 'celular_crianca', e.target.value)}
                    placeholder="Opcional" className={inputClass} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Observacoes</label>
                  <textarea data-testid={`filho-obs-${i}`} value={f.observacoes || ''}
                    onChange={e => updateFilho(i, 'observacoes', e.target.value)}
                    placeholder="Alergias, restricoes, informacoes importantes..." rows={2}
                    className={`${inputClass} resize-none`} />
                </div>
              </div>
            ))}
          </div>

          {/* Add new child */}
          {!showAddChild ? (
            <button onClick={() => setShowAddChild(true)}
              className="mt-4 text-[#1A4D2E] text-sm font-semibold flex items-center gap-1 hover:underline" data-testid="add-child-btn">
              <Plus className="w-4 h-4" /> Adicionar filho(a)
            </button>
          ) : (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#1A4D2E] uppercase">Novo Filho(a)</span>
                <button onClick={() => setShowAddChild(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Nome completo</label>
                <input data-testid="new-child-nome" value={newChild.nome} onChange={e => setNewChild(p => ({ ...p, nome: e.target.value }))}
                  placeholder="Nome da crianca" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Data de nascimento</label>
                  <input data-testid="new-child-nascimento" type="date" value={newChild.data_nascimento}
                    onChange={e => setNewChild(p => ({ ...p, data_nascimento: e.target.value }))} className={inputClass} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Sexo</label>
                  <select data-testid="new-child-sexo" value={newChild.sexo} onChange={e => setNewChild(p => ({ ...p, sexo: e.target.value }))} className={inputClass}>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                </div>
              </div>
              <button onClick={handleAddChild} disabled={saving}
                className="w-full bg-[#F4D35E] hover:bg-[#e8c74a] text-[#1A4D2E] font-nunito font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="confirm-add-child-btn">
                <Plus className="w-4 h-4" /> {saving ? 'Adicionando...' : 'Adicionar'}
              </button>
            </div>
          )}
        </Card>

        {/* Save button */}
        <button onClick={handleSave} disabled={saving}
          className="w-full bg-[#1A4D2E] hover:bg-[#2D6A3F] text-white font-nunito font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          data-testid="save-profile-btn">
          <Save className="w-5 h-5" /> {saving ? 'Salvando...' : 'Salvar Alteracoes'}
        </button>
      </div>
    </div>
  );
};

export default EditarPerfilFamilia;
